import React, { useState, useEffect } from "react";
import styles from "./styles.module.scss";
import { useNavigate, useLocation } from "react-router-dom";
// Config
import {
  mobileBreakpoint,
  SERVER,
  SUPERTOKEN_SERVER,
} from "../../Services/config";
// Utils
import { getInitialsFromNames } from "@intelligentlilli/lilli-utils";
// API
import {
  postUpdatePassword,
  postUpdateProperty,
  getOrganisation,
  postUserEmail,
  postLogout,
  OrganisationResponseType,
} from "@intelligentlilli/api-layer";
// Components
import Page from "../../Components/Page";
import CTAButton from "../../Components/CTAButton";
import { useViewport } from "../../Components/ViewportProvider";
import PrimaryButton from "../../Components/PrimaryButton";
import ServiceUserImage from "../../Components/ServiceUserImage";
import EditUserProfile from "../../Components/EditUserProfile";
import SecondaryButton from "../../Components/SecondaryButton";
import PageTitle from "../../Components/PageTitle";
// State
import { useDispatch, useSelector } from "react-redux";
import { setLoading, setRefreshUser } from "../../State/slices/session-slice";
import { setStaff } from "../../State/slices/staff-slice";
import { setServiceUsersData } from "../../State/slices/serviceUsersData-slice";
import { setUser } from "../../State/slices/user-slice";
import { setServiceUsers } from "../../State/slices/serviceUsers-slice";
// Icons
import { HomeIcon, PersonIcon, ReportsIcon } from "../../Styles/Icons";
// Hooks
import { useGetUserRole } from "../../Services/Hooks";
import { signOut } from "supertokens-auth-react/recipe/session";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";
import { reset } from "@amplitude/analytics-browser";
import ChangeOrganisation from "Components/ChangeOrganisation";

function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  // Get the width of the users screen for responsive js
  const { width } = useViewport();

  const track = useAmplitudeTracking();

  const trackSettings = async (eventPropertyString: string) => {
    const result = track("Web App Settings", { action: eventPropertyString });
    try {
      await result?.promise;

      if (eventPropertyString === "log_out") {
        reset(); // Reset user data after tracking "log_out"
      }
    } catch (error) {
      console.error("Error tracking event:", error);
    }
  };

  // Get whether the user is coming from the onboarding screens
  const onboarding = location.state?.onboarding;
  // redux state
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.authData);
  // Local state to hold API error messages
  const [changePasswordError, setChangePasswordError] = useState<null | string>(
    null
  );
  const [changeNameError, setChangeNameError] = useState<null | string>(null);
  const [changeEmailError, setChangeEmailError] = useState<null | string>(null);
  const [changePhoneNumError, setChangePhoneNumError] = useState<null | string>(
    null
  );
  const [editProfile, setEditProfile] = useState<boolean>(false);
  const [organisation, setOrganisation] = useState<
    OrganisationResponseType["data"] | null
  >(null);

  // Get the user's role
  const { userIsJustAnInstaller, userHasInstallerRole, userRoleAsAString } =
    useGetUserRole();
  const displayUserRole = userIsJustAnInstaller
    ? "Installer"
    : `${userRoleAsAString}${userHasInstallerRole ? " & Installer" : ""}`;

  // Get the organisation details
  useEffect(() => {
    if (user?.key) {
      dispatch(setLoading(true));
      getOrganisation(SERVER, user?.organisationId, "web")
        .then((res) => {
          dispatch(setLoading(false));
          if (res.ok) {
            setOrganisation(res?.data);
          }
        })
        .catch(() => {
          dispatch(setLoading(false));
        });
    }
  }, [dispatch, user?.organisationId, navigate, user?.key]);

  const onSubmitPasswordChange = async (
    currentPassword: string,
    newPassword: string
  ) => {
    setChangePasswordError(null);
    dispatch(setLoading(true));
    postUpdatePassword(SUPERTOKEN_SERVER, currentPassword, newPassword, "web")
      .then((res) => {
        dispatch(setLoading(false));
        if (res.status < 500 && res.status >= 400) {
          // TO DO: Fix this error handling

          const result = res.data;
          const message = result?.status;

          if (res.status === 403) {
            setChangePasswordError("Sorry, we could not change your password.");
            alert("Sorry, we could not change your password.");
          }

          if (res.status === 400) {
            if (message === "WRONG_CREDENTIALS_ERROR") {
              setChangePasswordError(
                "Sorry, we could not change your password."
              );
              alert("Sorry, we could not change your password.");
            }
          }

          setChangePasswordError(
            "We've encountered an error and could not change your password. Please try again"
          );
        }

        if (res.status === 200) {
          alert("Success: Password changed");
          trackSettings("change_password");
        }
      })
      .catch((err) => {
        dispatch(setLoading(false));
        console.log("Error with password change:", err);
      });
    return;
  };

  const onSubmitEmailChange = (email: string) => {
    setChangeEmailError(null);
    dispatch(setLoading(true));
    postUserEmail(SERVER, user.key, email, "web")
      .then((res) => {
        dispatch(setLoading(false));
        if (!res.ok) {
          setChangeEmailError(res?.data?.error); // This works - but different format to PW reset error
        } else {
          // Refresh the user so state represents the server again.
          dispatch(setRefreshUser(true));
          trackSettings("change_email");
        }
      })
      .catch((err) => {
        dispatch(setLoading(false));
        console.log("Email change error:", err);
      });
    return;
  };

  const onSubmitNameChange = async (forenames: string, surname: string) => {
    setChangeNameError(null);
    const names = [
      { key: "Forenames", value: forenames },
      { key: "Surname", value: surname },
    ];
    const promises = names.map((name) =>
      postUpdateProperty(SERVER, name.key, name.value, "web").then((res) => {
        if (!res.ok) {
          console.log("name change error:", res?.data);
          setChangeNameError("Sorry, we could not update your name");
        } else {
          trackSettings("change_name");
        }
      })
    );
    await Promise.all(promises)
      .then(() => {
        // Refresh the user so state represents the server again.
        dispatch(setRefreshUser(true));
        dispatch(setLoading(false));
      })
      .catch((err) => {
        dispatch(setLoading(false));
        setChangeEmailError("Error changing name");
      });
    return;
  };

  // Handle logging out. When successful, redirect them to the login page.
  const handleLogout = async () => {
    dispatch(setLoading(true));
    try {
      /* old log out */
      const response = await postLogout(SERVER, "web");

      if (!response.ok) {
        alert("Log out unsuccessful");
      } else {
        /* supertokens */
        await signOut().catch((err) => console.error("signOut failed", err));
        // Resetting redux back to it's initial state on logout
        navigate("/login", { state: { initialURL: "/" } });
        dispatch(setUser({}));
        dispatch(setServiceUsersData({}));
        dispatch(setServiceUsers([]));
        dispatch(setStaff([]));
        dispatch(setLoading(false));
      }
    } catch (err) {
      // TODO send alert to user that they cannot be logged out
      console.log("logout error:", err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Handle updating phone number
  const onSubmitPhoneNumChange = (value: string) => {
    setChangePhoneNumError("");
    dispatch(setLoading(true));
    postUpdateProperty(SERVER, "PrimaryPhone", value, "web")
      .then((res) => {
        dispatch(setLoading(false));
        if (!res.ok) {
          // TO DO: Fix this error handling
          setChangePhoneNumError(res?.data?.error);
        } else {
          // Refresh the user so state represents the server again.
          dispatch(setRefreshUser(true));
          trackSettings("change_phone_number");
        }
      })
      .catch(() => {
        dispatch(setLoading(false));
        alert("Error updating user preferences");
      });
  };

  return (
    <Page className={styles.page}>
      {onboarding ? (
        <PageTitle title="Welcome to Lilli" />
      ) : (
        <PageTitle title="Manage your account" />
      )}

      <div className={styles.content}>
        {!editProfile && (
          <>
            {onboarding && (
              <div className={styles.content_section}>
                <h2>
                  This is your profile page. Here you can change your details
                  and set your profile picture.
                </h2>
              </div>
            )}
            <div className={styles.content_section}>
              <div className={styles.profile}>
                <ServiceUserImage
                  initials={getInitialsFromNames(
                    user?.forenames,
                    user?.surname
                  )}
                  staff
                  largePill
                  className={styles.profile_image}
                />
                <div className={styles.profile_name}>
                  <h2>
                    {user?.forenames} {user?.surname}
                  </h2>
                  <PrimaryButton
                    startIcon={<PersonIcon />}
                    onClick={() => setEditProfile((state) => !state)}
                    className={styles.profile_edit_mobile}
                    style={width > mobileBreakpoint ? { display: "none" } : {}}
                  >
                    Edit profile
                  </PrimaryButton>
                </div>

                <div>
                  Email: <b>{user?.email}</b>
                </div>
                <div>
                  Roles: {displayUserRole}{" "}
                  {organisation?.name && `at ${organisation?.name}`}
                </div>
                <ChangeOrganisation />
              </div>

              <PrimaryButton
                startIcon={<PersonIcon />}
                onClick={() => setEditProfile((state) => !state)}
                className={styles.profile_edit}
                style={width <= mobileBreakpoint ? { display: "none" } : {}}
              >
                Edit profile
              </PrimaryButton>
            </div>

            {onboarding && (
              <div className={styles.content_section}>
                <div className={styles.notifications}>
                  <h3>
                    Once you're happy head over to the Reports page to choose
                    your Notifications and Reports settings.
                  </h3>

                  <PrimaryButton
                    onClick={() => navigate("/reports")}
                    className={styles.notifications_button}
                    startIcon={<ReportsIcon />}
                  >
                    Reports
                  </PrimaryButton>
                </div>
              </div>
            )}
            {onboarding && (
              <div className={styles.content_section}>
                <div className={styles.notifications}>
                  <h3>
                    Want to get started straight away?. Head over to your
                    dashboard to see who you've been assigned to look after
                  </h3>

                  <PrimaryButton
                    onClick={() => navigate("/")}
                    className={styles.notifications_button}
                    startIcon={<HomeIcon />}
                  >
                    Dashboard
                  </PrimaryButton>
                </div>
              </div>
            )}
            <CTAButton type="submit" onClick={handleLogout}>
              Logout
            </CTAButton>
            <div className={styles.buttons}>
              <SecondaryButton onClick={() => navigate("/faqs")}>
                FAQs
              </SecondaryButton>
              <SecondaryButton onClick={() => navigate("/about")}>
                About
              </SecondaryButton>
              <SecondaryButton onClick={() => navigate("/privacy")}>
                Privacy
              </SecondaryButton>
            </div>
          </>
        )}

        {editProfile && (
          <EditUserProfile
            close={() => setEditProfile(false)}
            user={user}
            onSubmitNameChange={onSubmitNameChange}
            onSubmitPasswordChange={onSubmitPasswordChange}
            onSubmitEmailChange={onSubmitEmailChange}
            onSubmitPhoneNumChange={onSubmitPhoneNumChange}
            changePasswordError={changePasswordError}
            changeNameError={changeNameError}
            changeEmailError={changeEmailError}
            changePhoneNumError={changePhoneNumError}
          />
        )}
      </div>
    </Page>
  );
}

export default Profile;
